import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { formatTime } from "@mp-commons/lib/utils/TimeUtils.js";
import { VideoContext } from "@mp-commons/modules/player/Player.js";
import { MegaridePlayerGUI } from "../../../../MegaridePlayer.js";
import { MediaControlOrigin } from "@mp-commons/modules/events/PlayerEvent.js";
import { ReplaySubject } from "@ciaopeople/cp-shatsu/Observable";
import { PARTHENOPE_GLOBAL_CSS_PREFIX } from "../../ParthenopeGUIV2.js";
import { VideoStoryBoard } from "@mp-commons/model/video/VideoInfo.js";

class StoryBoard {
    private mainHtml: HTMLElement;
    private preview: HTMLElement;
    private time: HTMLElement;
    private backgroundImgs: HTMLImageElement[] = [];
    private readonly tilesPerImage: number = 100;
    private readonly tilesPerRow: number = 10;
    private readonly tilesPerCol: number = 10;
    private readonly previewMainDimension: number = 120;
    private storyboardInfo: VideoStoryBoard;

    constructor(private videoContext: VideoContext) {


        this.initHtml();
        this.setupStoryboard();
    }

    public setVideoContext(ctx: VideoContext) {

        ctx.metadataPromise.then(_ => this.setupStoryboard())

        this.videoContext = ctx;

    }

    private previewBackground = (currentTime: number) => {
        let frame: { imgIndex: number, bgX: number, bgY: number, bgW: number, bgH: number, w: number, h: number } = null;
        const num = Math.floor(currentTime / this.storyboardInfo.gap);
        const file = Math.floor(num / this.tilesPerImage);
        const img = (file < this.backgroundImgs.length ? this.backgroundImgs[file] : null);
        if (img) {
            const effThumb = num % this.tilesPerImage;
            const row = Math.floor(effThumb / this.tilesPerRow);
            const col = effThumb % this.tilesPerRow;
            const frameW = Math.floor(img.naturalWidth / this.tilesPerRow);
            const frameH = Math.floor(img.naturalHeight / this.tilesPerCol);

            frame = {
                imgIndex: file,
                w: frameW,
                h: frameH,
                bgX: -col * frameW,
                bgY: -row * frameH,
                bgW: img.naturalWidth,
                bgH: img.naturalHeight
            }

        }
        return frame;
    };

    public show = (position: { timeValue: number, x: number }, xBoundary: { min: number, max: number }, changeThumbPreview: (evt) => void) => {
        if (this.storyboardInfo) {
            this.time.innerHTML = formatTime(position.timeValue);

            const frame = this.previewBackground(position.timeValue,);
            if (frame) {
                const bgImg: string = this.backgroundImgs[frame.imgIndex]?.src;
                if (bgImg) {

                    const scaleX = this.preview.clientWidth / frame.w;
                    const scaleY = this.preview.clientHeight / frame.h;

                    this.preview.style.background = `url("${bgImg}") ${frame.bgX * scaleX}px ${frame.bgY * scaleY}px / ${frame.bgW * scaleX}px ${frame.bgH * scaleY}px`;
                    const right = position.x + this.mainHtml.clientWidth / 2;
                    const left = position.x - this.mainHtml.clientWidth / 2;
                    const normalizedLeft = left < xBoundary.min ? xBoundary.min : (right > xBoundary.max ? left - (right - xBoundary.max) : left);
                    this.mainHtml.style.left = `${normalizedLeft}px`;
                    this.toggleVisibility(true);

                    if (changeThumbPreview)
                        changeThumbPreview({
                            bgImg: bgImg,
                            frame: frame,
                            position: position
                        })
                }
            }
        }
    };

    private toggleVisibility(visible: boolean): void {
        if (visible) {
            this.mainHtml.style.visibility = "visible";
            this.mainHtml.style.opacity = "1";
        } else {
            this.mainHtml.style.visibility = "hidden";
            this.mainHtml.style.opacity = "0";
        }
    }

    public hide = (evt: any, changeThumbPreview: (evt) => void) => {
        this.toggleVisibility(false);

        if (changeThumbPreview)
            changeThumbPreview(null)
    }

    private setupStoryboard() {

        const formatStoryboardUrls = (uri: string, replace: number[]) => {
            const placeholder = '%d'

            let result = uri;
            for (let i = 0; i < replace.length; i++) {
                const regex = RegExp(`^(?:.*?${placeholder}){1}`)
                result = result.replace(regex, function (x) { return x.replace(RegExp(`${placeholder}$`), `${replace[i]}`) })

            }

            if (result == uri) {
                console.error(`Invalid Storyboard url ${uri}`)
                return null
            } else {
                return result
            }
        }

        this.storyboardInfo = this.videoContext?.ref?.info?.storyboard;
        const duration = this.videoContext?.duration || 0;

        const storyBoardSources = [];
        if (this.storyboardInfo && this.storyboardInfo.gap && this.storyboardInfo.url) {
            const howManyTiles = Math.floor(duration / this.storyboardInfo.gap);
            const howManyFiles = Math.ceil(howManyTiles / this.tilesPerImage);

            for (let i = 1; i <= howManyFiles; i++) {
                const source = formatStoryboardUrls(this.storyboardInfo.url, [0, i])
                if (source)
                    storyBoardSources.push(source);
            }
        }

        const getImg = (url, callback) => {
            const img = new Image();
            img.onload = () => callback(null, img);
            img.onerror = (err) => callback(err);
            img.src = url;
            return img
        };

        Promise.all(storyBoardSources.map(src => {
            return getImg(src, (err, img) => {
                return img;
            });
        }))
            .then(images => this.backgroundImgs = images)
            .catch(err => this.backgroundImgs = [])

        const aspectRatio = this.videoContext?.getCurrentFormat().parsedSize.aspectRatio;

        if (aspectRatio) {
            if (aspectRatio.isHorizontal()) {
                const previewSecondaryDimension = this.previewMainDimension / aspectRatio.ratio;
                this.mainHtml.style.width = `${this.previewMainDimension}px`;
                this.mainHtml.style.height = `${previewSecondaryDimension + this.time.clientHeight}px`;
            } else {
                const previewSecondaryDimension = this.previewMainDimension * aspectRatio.ratio;
                this.mainHtml.style.height = `${this.previewMainDimension + this.time.clientHeight}px`;
                this.mainHtml.style.width = `${previewSecondaryDimension}px`;
            }
        }
    }

    get html(): HTMLElement {
        return this.mainHtml
    }

    private initHtml(): void {
        const bClass = `${PARTHENOPE_GLOBAL_CSS_PREFIX}-story-box`

        this.mainHtml = <div class={`${bClass}`}>
            <div class={`${bClass}__top`} ref={(el: HTMLElement) => this.preview = el}></div>
            <div class={`${bClass}__bottom`} ref={(el: HTMLElement) => this.time = el}></div>
        </div>
    }

}



export class ControlProgress {

    private mainHtml: HTMLElement;

    private storyBoard: StoryBoard;
    private sliderProgress: HTMLElement;
    private currentProgress: HTMLElement;
    private bufferProgress: HTMLElement;


    public active: ReplaySubject<boolean> = new ReplaySubject<boolean>(1, false);
    private duration: number = 0;
    private wvDetect: DetectedWebView;

    private containerAbsolutePosition: DOMRect;

    private updateContainerAbsolutePosition() {
        this.containerAbsolutePosition = this.gui.playerEl.getBoundingClientRect();
    }

    private changeThumbPreview: ((evt) => void) = null

    private get isDesktop(): boolean {
        return this.wvDetect.device?.other
    }

    private evtListeners: {
        timeupdate: EventListener,
        buffered: EventListener
    }

    private get isFocused() {
        return this.gui.playerEl.classList.contains("ymp--focus")
    }

    constructor(private videoContext: VideoContext,
        private gui: MegaridePlayerGUI,
        private props: {
            enableSeek: boolean,
            enableStoryboard: boolean,
            isAdv: boolean
        }) {

        this.storyBoard = null;
        this.sliderProgress = null;
        this.currentProgress = null;
        this.bufferProgress = null;

        this.wvDetect = detectWebView();
        this.evtListeners = {
            timeupdate: null,
            buffered: null,
        }
        this.initHtml();

    }

    signalAdProgress(time: number, duration: number): void {
        if (this.props.isAdv) {
            this.duration = duration
            this.setProgress(time)
        }
    }

    public setVideoContext(ctx: VideoContext) {

        if (!this.props.isAdv) {
            const timeupdateListener = (evt) => {
                this.setProgress(this.videoContext.currentTime)
            };

            const bufferedListener = (evt) => {
                const vid = ctx.ref.element;
                if (vid.buffered.length > 0)
                    this.updateBufferProgress(vid.buffered.end(vid.buffered.length - 1))
            };

            ctx.on("timeupdate", timeupdateListener);
            ctx.on("progress", bufferedListener);
            ctx.metadataPromise.then(_ => this.initProgress());


            if (this.evtListeners?.timeupdate) this.videoContext.off("timeupdate", this.evtListeners.timeupdate);
            if (this.evtListeners?.buffered) this.videoContext.off("progress", this.evtListeners.buffered);


            this.evtListeners.timeupdate = timeupdateListener;
            this.evtListeners.buffered = bufferedListener;

        }

        this.videoContext = ctx;

        if(this.storyBoard){
            this.storyBoard.setVideoContext(this.videoContext)
        }

    }

    public onChangeThumbPreview(listener: (evt) => void) {
        this.changeThumbPreview = listener
    }

    private updateBufferProgress(buffered: number) {
        const normalizedBufferd = buffered < 0 ? 0 : (buffered > 100 ? 100 : buffered);
        this.bufferProgress.style.width = `${normalizedBufferd}%`
    }

    private initProgress() {
        this.duration = this.videoContext?.duration
        this.setProgress(0)
    }

    private setProgress(time: number): void {
        const progressRounded = Math.min((time * 100) / this.duration, 100);
        this.currentProgress.style.width = progressRounded + "%"
    }

    private attachSeekListener(): void {
        let lastTime = null;
        let paused = false;

        const calcEventPosition = (evt: any) => {
            const localX = (window.TouchEvent && evt instanceof TouchEvent ? (evt.targetTouches[0] || evt.changedTouches[0])?.clientX || 0 : evt.clientX) - this.mainHtml.offsetLeft - this.containerAbsolutePosition?.left || 0;
            const normalizedLocalX = localX < 0 ? 0 : (localX > this.sliderProgress.clientWidth ? this.sliderProgress.clientWidth : localX)
            lastTime = normalizedLocalX * (this.duration / this.sliderProgress.firstElementChild.clientWidth);
            return { timeValue: lastTime, x: normalizedLocalX };
        };

        const showStoryboard = (evt: any, changeThumbPreview: any) => {
            const position = calcEventPosition(evt);
            if(this.storyBoard) {
                this.storyBoard.show(position, { min: 0, max: this.sliderProgress.clientWidth }, changeThumbPreview);
            }
            return position;
        }

        const seekingHandler = (evt) => {
            evt.preventDefault();

            if (this.active.getCurrentValue()) {
                const position = showStoryboard(evt, this.changeThumbPreview);
                this.currentProgress.style.width = Math.min((position.timeValue * 100) / this.duration, 100) + "%";
            }
        };

        const endSeekHandler = (evt) => {
            if (this.isFocused) {

                const onSeek = (currentTime: number) => {
                    this.videoContext.seek(currentTime);
                    if (!paused)
                        this.videoContext.togglePlay(false);
                }

                evt.preventDefault();
                this.active.next(false);

                if(this.storyBoard) {
                    this.storyBoard.hide(evt, this.changeThumbPreview)
                }

                if (this.isDesktop) {
                    this.gui.window.removeEventListener("mousemove", seekingHandler);
                    this.gui.window.removeEventListener("mouseup", endSeekHandler);
                } else {
                    this.gui.window.removeEventListener("touchmove", seekingHandler)
                    this.gui.window.removeEventListener("touchend", endSeekHandler)
                }

                onSeek(calcEventPosition(evt).timeValue);

            }

            return false

        };

        const startSeekHandler = (evt) => {
            if (this.isFocused) {
                paused = this.videoContext.isPaused
                evt.preventDefault();
                this.updateContainerAbsolutePosition();
                this.active.next(true);
                this.videoContext.pause(MediaControlOrigin.NON_USER)
                lastTime = this.currentProgress.clientWidth;
                if (this.isDesktop) {
                    this.gui.window.addEventListener("mousemove", seekingHandler, { passive: false });
                    this.gui.window.addEventListener("mouseup", endSeekHandler, { passive: false });
                } else {
                    this.gui.window.addEventListener("touchmove", seekingHandler, { passive: false })
                    this.gui.window.addEventListener("touchend", endSeekHandler, { passive: false })
                }
            }
        }



        const hoverHandler = (evt) => {
            if (this.isFocused) {
                evt.preventDefault();
                this.updateContainerAbsolutePosition();
                lastTime = this.currentProgress.clientWidth;

                this.sliderProgress.addEventListener("mousemove", (evt) => {
                    evt.preventDefault();
                    showStoryboard(evt, null);
                }, { passive: false });

                this.sliderProgress.addEventListener("mouseleave", (evt) => {
                    if(this.storyBoard) this.storyBoard.hide(evt, null)
                }, { passive: false });

            }
        }

        if (this.isDesktop) {
            this.sliderProgress.addEventListener("mouseenter", hoverHandler)
            this.sliderProgress.addEventListener("mousedown", startSeekHandler, { passive: false })
        } else {
            this.sliderProgress.addEventListener("touchstart", startSeekHandler, { passive: false })
        }


    }


    private initHtml(): void {
        const bClass = `${PARTHENOPE_GLOBAL_CSS_PREFIX}-lb`;

        if (this.props.enableStoryboard) {
            this.storyBoard = new StoryBoard(this.videoContext);
        }

        this.mainHtml = <div class={`${bClass}`}>
            <div class={`${bClass} ${bClass}--video`} ref={(el: HTMLElement) => this.sliderProgress = el}>
                <div class={`${bClass}__inner`}>
                    <div class={`${bClass}__in ${bClass}__in--buffer`} ref={(el: HTMLElement) => this.bufferProgress = el}></div>
                    <div class={`${bClass}__in ${bClass}__in--current`} ref={(el: HTMLElement) => this.currentProgress = el}></div>
                </div>
                {this.storyBoard?.html}
            </div>
        </div>

        if (this.props.enableSeek) {
            this.attachSeekListener();
        }
    }

    get html(): HTMLElement {
        return this.mainHtml
    }

}
